'use strict'

const Big = require('big.js').default
const erc20Abi = require('erc-20-abi')
const parseReceiptEvents = require('web3-parse-receipt-events')
const debug = require('debug')('metronome-esmet-lib')
const esMETAbi = require('./abi/ESMET.json')
const esMET721Abi = require('./abi/ESMET721.json')
const rewardsAbi = require('./abi/rewards.json')
const pTap = require('p-tap')

const createExecutor = require('transactions-executor')
const { findByChainId } = require('chain-list')
const { findTokenByAddress } = require('token-list')
const { fromUnit, handleForkedChainId } = require('metronome-utils')

class ChainNotSupportedError extends Error {
  constructor(message) {
    super(message)
    this.name = 'ChainNotSupportedError'
  }
}

const createMETLocker = function (web3, options = {}) {
  const { chainId: _chainId, from } = options
  const esMETAddresses = {
    1: '0xA28D70795a61Dc925D4c220762A4344803876bb8'
  }

  const chainIdPromise = _chainId
    ? Promise.resolve(_chainId)
    : web3.eth.getChainId()

  return chainIdPromise.then(handleForkedChainId).then(function (chainId) {
    const esMETAddress = esMETAddresses[chainId]

    if (!esMETAddress) {
      throw new ChainNotSupportedError()
    }
    const esMETContract = new web3.eth.Contract(esMETAbi, esMETAddress)
    const getesMET721Address = () => esMETContract.methods.esMET721().call()
    const getRewardsAddress = () => esMETContract.methods.rewards().call()
    const getMETAddress = () => esMETContract.methods.MET().call()

    return Promise.all([
      getesMET721Address(),
      getRewardsAddress(),
      getMETAddress()
    ]).then(function ([esMET721Address, rewardsAddress, METAddress]) {
      const expectedGasFor = {
        approval: 47000,
        claim: 30000000,
        dripRewards: 30000000,
        lock: 390000,
        unlock: 150000
      }

      const esMET721Contract = new web3.eth.Contract(
        esMET721Abi,
        esMET721Address
      )
      const rewardsContract = new web3.eth.Contract(rewardsAbi, rewardsAddress)
      const METContract = new web3.eth.Contract(erc20Abi, METAddress)

      const calculateExitPenalty = tokenId =>
        esMETContract.methods.calculateExitPenalty(tokenId).call()

      const getExitPenalty = () => esMETContract.methods.exitPenalty().call()
      const getMaximumBoost = () => esMETContract.methods.MAXIMUM_BOOST().call()
      const getMaximumLockPeriod = () =>
        esMETContract.methods.MAXIMUM_LOCK_PERIOD().call()

      // @TODO: This method should also return price. Right now it's not supported by the Master Oracle. But a new oracle will be deployed and this method should be updated.
      const getMETInfo = () =>
        Promise.all([
          METContract.methods.decimals().call(),
          METContract.methods.symbol().call()
        ]).then(([decimals, symbol]) => ({
          address: METAddress,
          decimals,
          symbol
        }))

      const getEsMETInfo = () =>
        Promise.all([
          esMETContract.methods.decimals().call(),
          esMETContract.methods.symbol().call()
        ]).then(([decimals, symbol]) => ({
          address: esMETAddress,
          decimals,
          symbol
        }))

      const getMinimumLockPeriod = () =>
        esMETContract.methods.MINIMUM_LOCK_PERIOD().call()

      const getTotalBoosted = () => esMETContract.methods.totalBoosted().call()
      const getTotalLocked = () => esMETContract.methods.totalLocked().call()
      const getTotalSupply = () => esMETContract.methods.totalSupply().call()

      const getClaimables = () =>
        rewardsContract.methods
          .claimableRewards(from)
          .call()
          .then(({ _rewardTokens, _claimableAmounts }) =>
            _rewardTokens.map((rewardToken, idx) => ({
              claimableAmount: _claimableAmounts[idx],
              rewardToken
            }))
          )

      const getRewardsData = () =>
        rewardsContract.methods
          .rewardTokens(0)
          .call()
          .then(rewardToken =>
            rewardsContract.methods.rewards(rewardToken).call()
          )
          .then(({ isBoosted, periodFinish, rewardPerSecond }) => ({
            isBoosted,
            periodFinish,
            rewardPerSecond
          }))
          .catch(() => null) // Return null if no rewards configured

      const getLockedMET = () =>
        esMETContract.methods
          .locked(from)
          .call()
          .then(
            pTap(balance =>
              debug('Locked balance for %s is %s MET', from, balance)
            )
          )

      const getLockedPositions = () =>
        esMET721Contract.methods
          .balanceOf(from)
          .call()
          .then(function (totalPositions) {
            const tokenIdPromises = []
            for (let i = 0; i < totalPositions; i++) {
              tokenIdPromises.push(
                esMET721Contract.methods.tokenOfOwnerByIndex(from, i).call()
              )
            }
            return Promise.all(tokenIdPromises)
          })
          .then(function (tokenIds) {
            const positionPromises = tokenIds.map(tokenId =>
              Promise.all([
                esMETContract.methods.positions(tokenId).call(),
                esMETContract.methods.getLockedPeriodOf(tokenId).call()
              ]).then(function ([position, lockedPeriod]) {
                const { boostedAmount, lockedAmount, unlockTime } = position
                const lockTimestamp = unlockTime - lockedPeriod
                const minimumUnlockTime = lockTimestamp + 86400 // 1 day
                debug(
                  'Position %s found for address %s with %s locked and %s boosted for %s ending in %s with early withdraw avaialble in %s',
                  tokenId,
                  from,
                  lockedAmount,
                  boostedAmount,
                  lockedPeriod,
                  unlockTime,
                  minimumUnlockTime
                )
                return {
                  boostedAmount,
                  lockedAmount,
                  lockedPeriod,
                  minimumUnlockTime,
                  tokenId,
                  unlockTime
                }
              })
            )
            return Promise.all(positionPromises)
          })

      const getBoostedMET = () =>
        esMETContract.methods
          .boosted(from)
          .call()
          .then(
            pTap(balance =>
              debug('Boosted balance for %s is %s MET', from, balance)
            )
          )

      const executeTransactions = createExecutor({
        chainId,
        from,
        overestimation: findByChainId(chainId).gasOverestimation,
        web3
      })

      const findReturnValue = (receipt, eventName, prop, address, to) =>
        []
          .concat(receipt.events[eventName] || [])
          .filter(
            event =>
              !address || event.address.toLowerCase() === address.toLowerCase()
          )
          .filter(
            event =>
              !to || event.returnValues.to.toLowerCase() === to.toLowerCase()
          )
          .map(event => event.returnValues[prop])[0]

      const isApprovalNeeded = function ({
        amount,
        owner,
        spender,
        token,
        tokenContract
      }) {
        const { decimals, symbol } = findTokenByAddress(token, chainId)
        debug(
          'Checking if approval is needed for %s %s',
          fromUnit(amount, decimals),
          symbol
        )
        return tokenContract.methods
          .allowance(owner, spender)
          .call()
          .then(function (allowance) {
            debug(
              'Allowance for %s is %s',
              symbol,
              fromUnit(allowance, decimals)
            )
            const approvalNeeded = new Big(allowance).lt(amount)
            debug('Approval is %s', approvalNeeded ? 'needed' : 'not needed')
            return approvalNeeded
          })
      }

      const addMETRewards = function (amount, transactionOptions = {}) {
        const owner = transactionOptions.from || from
        debug(
          'Adding %s MET(%s) rewards from %s to %s rewards contract',
          amount,
          METAddress,
          owner,
          rewardsAddress
        )
        const transactionsPromise = isApprovalNeeded({
          amount,
          owner,
          spender: rewardsAddress,
          token: METAddress,
          tokenContract: METContract
        }).then(function (approvalNeeded) {
          const txs = []
          if (approvalNeeded) {
            txs.push({
              gas: expectedGasFor.approval,
              method: METContract.methods.approve(rewardsAddress, amount),
              suffix: 'approve'
            })
          }
          txs.push({
            gas: expectedGasFor.dripRewards,
            method: rewardsContract.methods.dripRewardAmount(
              METAddress,
              amount
            ),
            suffix: 'drip'
          })
          return txs
        })

        return executeTransactions(
          transactionsPromise,
          () => null,
          transactionOptions
        )
      }

      const claim = function (transactionOptions = {}) {
        const owner = transactionOptions.from || from
        debug('Initiating claim for %s', owner)

        const transactionsPromise = Promise.resolve([
          {
            gas: expectedGasFor.claim,
            method: rewardsContract.methods.claimRewards(owner),
            suffix: 'claim'
          }
        ])

        const parseResults = function ([transactionData]) {
          const { receipt } = transactionData
          const claimed = {
            rewardToken: findReturnValue(receipt, 'RewardPaid', 'rewardToken'),
            type: 'erc20',
            value: findReturnValue(receipt, 'RewardPaid', 'reward')
          }
          return { claimed }
        }

        return executeTransactions(
          transactionsPromise,
          parseResults,
          transactionOptions
        )
      }

      const lock = function (amount, period, transactionOptions = {}) {
        debug('Initiating lock of %s MET for %s', amount, period)
        const owner = transactionOptions.from || from
        const transactionsPromise = isApprovalNeeded({
          amount,
          owner,
          spender: esMETAddress,
          token: METAddress,
          tokenContract: METContract
        }).then(function (approvalNeeded) {
          const txs = []
          if (approvalNeeded) {
            txs.push({
              gas: expectedGasFor.approval,
              method: METContract.methods.approve(esMETAddress, amount),
              suffix: 'approve'
            })
          }
          txs.push({
            gas: expectedGasFor.lock,
            method: esMETContract.methods.lock(amount, period),
            suffix: 'lock'
          })
          return txs
        })

        const parseResults = function (transactionsData) {
          const { receipt } = transactionsData[transactionsData.length - 1]
          parseReceiptEvents(esMET721Abi, esMET721Address, receipt)
          parseReceiptEvents(esMETAbi, esMETAddress, receipt)
          const sent = {
            type: 'erc20',
            value: findReturnValue(receipt, 'Transfer', 'value', METAddress)
          }
          const received = [
            {
              type: 'erc20',
              value: findReturnValue(receipt, 'Transfer', 'value', esMETAddress)
            },
            {
              type: 'erc721',
              value: findReturnValue(
                receipt,
                'Transfer',
                'tokenId',
                esMET721Address
              )
            }
          ]
          return { received, sent }
        }

        return executeTransactions(
          transactionsPromise,
          parseResults,
          transactionOptions
        )
      }

      const unlock = function (
        tokenId,
        earlyUnlock = false,
        transactionOptions = {}
      ) {
        const owner = transactionOptions.from || from
        debug('Initiating unlock of NFT id %s for %s', tokenId, owner)
        const transactionsPromise = Promise.resolve([
          {
            gas: expectedGasFor.unlock,
            method: esMETContract.methods.unlock(tokenId, earlyUnlock),
            suffix: 'unlock'
          }
        ])

        const parseResults = function (transactionsData) {
          const { receipt } = transactionsData[transactionsData.length - 1]
          parseReceiptEvents(esMET721Abi, esMET721Address, receipt)
          const sent = [
            {
              type: 'erc20',
              value: findReturnValue(receipt, 'Transfer', 'value', esMETAddress)
            },
            {
              type: 'erc721',
              value: findReturnValue(
                receipt,
                'Transfer',
                'tokenId',
                esMET721Address
              )
            }
          ]
          const received = {
            type: 'erc20',
            value: findReturnValue(
              receipt,
              'Transfer',
              'value',
              METAddress,
              owner
            )
          }
          const penalty = {
            type: 'erc20',
            value: findReturnValue(receipt, 'MetUnlocked', 'penalty')
          }
          return { penalty, received, sent }
        }

        return executeTransactions(
          transactionsPromise,
          parseResults,
          transactionOptions
        )
      }

      const informationPromises = [
        getEsMETInfo(),
        getExitPenalty(),
        getMaximumBoost(),
        getMaximumLockPeriod(),
        getMETInfo(),
        getMinimumLockPeriod(),
        getTotalBoosted(),
        getTotalLocked()
      ]

      const getInfo = () =>
        Promise.all(informationPromises).then(
          ([
            esMet,
            exitPenalty,
            maximumBoost,
            maximumLockPeriod,
            met,
            minimumLockPeriod,
            totalBoosted,
            totalLocked
          ]) => ({
            esMet,
            exitPenalty,
            maximumBoost,
            maximumLockPeriod,
            met,
            minimumLockPeriod,
            totalBoosted,
            totalLocked
          })
        )

      const getMETBalance = () => METContract.methods.balanceOf(from).call()
      const getEsMETBalance = () => esMETContract.methods.balanceOf(from).call()

      return {
        addMETRewards,
        calculateExitPenalty,
        claim,
        getBoostedMET,
        getClaimables,
        getEsMETBalance,
        getInfo,
        getLockedMET,
        getLockedPositions,
        getMETBalance,
        getMETInfo,
        getRewardsData,
        getTotalSupply,
        isApprovalNeeded,
        lock,
        unlock
      }
    })
  })
}

module.exports = {
  createMETLocker,
  ChainNotSupportedError
}
